import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Stack,
  HStack,
  Button,
  useToast
} from '@chakra-ui/react';
import Form from '@rjsf/chakra-ui';
import validator from "@rjsf/validator-ajv8";
import { reverseTransformData, transformData } from '../../../util/helper';
import { getLocationData, updateLocationGeographicalDetailsData, getStatesData } from '../../../services/Location';
import { getLanguageAliasSuggestions } from '../../../services/Color';
import FormLoader from '../../../CommonComponents/FormLoader';
import TypeaheadWidget from '../../../CommonComponents/TypeaheadWidget';
import { showErrorToast, showInfoToast, showSuccessToast } from "../../../util/errorHandler";


const GeographicalDetails = () => {
  const { alias } = useParams();
  const [leftFormData, setLeftFormData] = useState({});
  const [rightFormData, setRightFormData] = useState({});
  const [combinedFormData, setCombinedFormData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quickSave, setQuickSave] = useState(false);
  const toast = useToast();
  const previewEnabled = useSelector(state => state.preview.isEnabled);
  
  const formRef = useRef(null);

  // Define array fields that need type values (add, update, delete)
  const arrayFields = ['languages', 'lakes', 'states'];
  const simpleFields = ['latitude', 'longitude', 'population', 'current_area', 'rivers'];

  // Left Column Schema - Basic Fields
  const leftColumnSchema = {
    "title": "",
    "description": "",
    "type": "object",
    "properties": {
      latitude: {
        type: 'string',
        title: 'Latitude',
      },
      longitude: {
        type: 'string',
        title: 'Longitude',
      },
      population: {
        type: 'string',
        title: 'Population',
      },
      current_area: {
        type: 'string',
        title: 'Current Area',
      },
      rivers: {
        type: 'string',
        title: 'Rivers',
      },
      "states": {
        "type": "string",
        "title": "States"
      },
    }
  }

  // Right Column Schema - Array Fields
  const rightColumnSchema = {
    "title": "",
    "description": "",
    "type": "object",
    "properties": {
      "languages": {
        type: 'array',
        title: 'Languages',
        items: {
          type: 'object',
          properties: {
            "alias": {
              type: 'string',
              title: 'Alias'
            },
          }
        }
      },
      "lakes": {
        type: 'array',
        title: 'Lakes',
        items: {
          type: 'object',
          properties: {
            "alias": {
              type: 'string',
              title: 'Alias'
            },
          }
        }
      },
    }
  }

  // Combined Schema for Single Column (Preview Mode)
  const combinedSchema = {
    "title": "GeographicalDetails fields",
    "description": "",
    "type": "object",
    "properties": {
      latitude: {
        type: 'string',
        title: 'Latitude',
      },
      longitude: {
        type: 'string',
        title: 'Longitude',
      },
      population: {
        type: 'string',
        title: 'Population',
      },
      current_area: {
        type: 'string',
        title: 'Current Area',
      },
      rivers: {
        type: 'string',
        title: 'Rivers',
      },
      "states": {
        "type": "string",
        "title": "States"
      },
      "languages": {
        type: 'array',
        title: 'Languages',
        items: {
          type: 'object',
          properties: {
            "alias": {
              type: 'string',
              title: 'Alias'
            },
          }
        }
      },
      "lakes": {
        type: 'array',
        title: 'Lakes',
        items: {
          type: 'object',
          properties: {
            "alias": {
              type: 'string',
              title: 'Alias'
            },
          }
        }
      },
    }
  }

  // Left Column UI Schema
  const leftColumnUiSchema = (isSubmitting) => ({
    "ui:submitButtonOptions": {
      "norender": true
    },
    "ui:style": {
      fontSize: "14px",
    },
    latitude: {
      "ui:emptyValue": ""
    },
    longitude: {
      "ui:emptyValue": ""
    },
    population: {
      "ui:emptyValue": ""
    },
    current_area: {
      "ui:emptyValue": ""
    },
    rivers: {
      "ui:emptyValue": ""
    },
    states: {
      "ui:emptyValue": "",
      "ui:widget": "typeahead"
    },
  });

  // Right Column UI Schema
  const rightColumnUiSchema = (isSubmitting) => ({
    "ui:submitButtonOptions": {
      "norender": true
    },
    "ui:style": {
      fontSize: "14px",
    },
    languages: {
      items: {
        alias: {
          "ui:widget": "typeahead2",
        },
      },
    },
    lakes: {
      "ui:emptyValue": ""
    }
  });

  // Combined UI Schema for Single Column (Preview Mode)
  const combinedUiSchema = (isSubmitting) => ({
    "ui:submitButtonOptions": {
      "norender": true
    },
    "ui:style": {
      fontSize: "14px",
    },
    latitude: {
      "ui:emptyValue": ""
    },
    longitude: {
      "ui:emptyValue": ""
    },
    population: {
      "ui:emptyValue": ""
    },
    current_area: {
      "ui:emptyValue": ""
    },
    rivers: {
      "ui:emptyValue": ""
    },
    states: {
      "ui:emptyValue": "",
      "ui:widget": "typeahead"
    },
    languages: {
      items: {
        alias: {
          "ui:widget": "typeahead2",
        },
      },
    },
    lakes: {
      "ui:emptyValue": ""
    }
  });

  const loadLanguageAliasSuggestions = async (inputValue) => {
    try {
      const result = await getLanguageAliasSuggestions(20, 1, inputValue);
      return result?.data?.data || [];
    } catch (error) {
      console.error('Error fetching language suggestions:', error);
            showErrorToast(toast, "Failed to load language suggestions");
      return [];
    }
  };

  const fetchLanguageAliasSuggestions = async (inputValue) => {
    try {
      const fetchedLanguageSuggestions = await loadLanguageAliasSuggestions(inputValue);
      if (!fetchedLanguageSuggestions) {
        return [];
      }
      return fetchedLanguageSuggestions?.map((item) => ({
        label: item?.alias || item?.name || '',
        value: item?.alias || item?.name || ''
      }));
    } catch (error) {
      console.error('Error in fetchLanguageAliasSuggestions:', error);
      return [];
    }
  };

  const widgets = {
    typeahead: (props) => (
      <TypeaheadWidget
        {...props}
        fetchOptions={getStatesData}
      />
    ),
    typeahead2: (props) => (
      <TypeaheadWidget
        {...props}
        fetchOptions={fetchLanguageAliasSuggestions}
      />
    ),
  };

  // Function to identify changed items for array fields
  const getChangedItems = (currentData, originalData, fieldName) => {
    const currentArray = currentData[fieldName] || [];
    const originalArray = originalData[fieldName] || [];
    
    const changedItems = [];
    const processedOriginalAliases = new Set();

    // Create a map of original items by alias for efficient lookup
    const originalMap = new Map();
    originalArray.forEach(item => {
      if (item.alias) {
        originalMap.set(item.alias, item);
      }
    });

    // Check current items against original
    currentArray.forEach(currentItem => {
      if (currentItem.alias && originalMap.has(currentItem.alias)) {
        // Item exists in original - check if it's been updated
        const originalItem = originalMap.get(currentItem.alias);
        
        // Compare each field individually
        const hasChanges = Object.keys(currentItem).some(key => {
          return currentItem[key] !== originalItem[key];
        });
        
        if (hasChanges) {
          // This is an updated item
          changedItems.push({
            ...currentItem,
            type: 'update'
          });
        }
        
        processedOriginalAliases.add(currentItem.alias);
      } else {
        // No alias or not in original - this is a new item
        changedItems.push({
          ...currentItem,
          type: 'add'
        });
      }
    });

    // Check for deleted items (items that exist in original but not in current)
    originalArray.forEach(originalItem => {
      if (originalItem.alias && !processedOriginalAliases.has(originalItem.alias)) {
        // This item doesn't exist in current data - it's deleted
        changedItems.push({
          ...originalItem,
          type: 'delete'
        });
      }
    });
    
    return changedItems;
  };

  // Split data for two-column layout
  const splitFormData = (data = {}) => {
    const leftFields = ['latitude', 'longitude', 'population', 'current_area', 'rivers', 'states'];
    const rightFields = ['languages', 'lakes'];
    
    const leftData = {};
    const rightData = {};
    
    // Process left fields with defaults
    leftFields.forEach(field => {
      if (data.hasOwnProperty(field)) {
        leftData[field] = data[field];
      } else {
        leftData[field] = "";
      }
    });
    
    // Process right fields with defaults
    rightFields.forEach(field => {
      if (data.hasOwnProperty(field)) {
        rightData[field] = data[field];
      } else {
        rightData[field] = [];
      }
    });
      
    return { leftData, rightData };
  };
  
  // Combine data from both forms
  const combineFormData = (leftData, rightData) => {
    return { ...leftData, ...rightData };
  };

  // Handle form changes for two-column layout
  const handleLeftFormChange = (data) => {
    setLeftFormData(data.formData);
  };
  
  const handleRightFormChange = (data) => {
    setRightFormData(data.formData);
  };

  const handleCombinedFormChange = (data) => {
    setCombinedFormData(data.formData);
  };

  // Fetch initial data
  useEffect(() => {
    const getGeographicalDetailsData = async () => {
      try {
        if (alias) {
          const result = await getLocationData(alias, 'introduction', 'geographical-details');
          if (result) {
            const fieldsToTransform = [
            ];
            const transformedData = transformData(result?.[0], fieldsToTransform);
            
            // Store original array fields as arrays for comparison
            const originalDataForComparison = { ...transformedData };
            
            // Store original states as array for comparison
            const originalStatesArray = transformedData.states && Array.isArray(transformedData.states) 
              ? [...transformedData.states] 
              : [];
            
            // Transform states from array of objects to string for display
            if (transformedData.states && Array.isArray(transformedData.states) && transformedData.states.length > 0) {
              // Take the first state's name or alias as the string value
              transformedData.states = transformedData.states[0].name || transformedData.states[0].alias || '';
            }
            
            // Ensure all array fields are stored as arrays in originalData for proper comparison
            arrayFields.forEach(fieldName => {
              if (fieldName === 'states') {
                originalDataForComparison[fieldName] = originalStatesArray;
              } else {
                // For languages and lakes, ensure they're arrays
                originalDataForComparison[fieldName] = Array.isArray(transformedData[fieldName]) 
                  ? [...transformedData[fieldName]] 
                  : [];
              }
            });
            
            // Split data for two-column layout
            const { leftData, rightData } = splitFormData(transformedData);
            setLeftFormData(leftData);
            setRightFormData(rightData);
            setCombinedFormData(transformedData);
            setOriginalData(originalDataForComparison); // Store original data with all array fields as arrays for comparison
            setQuickSave(false);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
                showErrorToast(toast, "Failed to fetch data");
      } finally {
        setIsLoading(false);
      }
    };

    getGeographicalDetailsData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alias, toast, quickSave]);

  // Submit updated data
  const handleSubmit = async () => {
    try {
      if (alias) {
        setIsSubmitting(true);
        
        // Use combined form data in preview mode, otherwise combine left and right forms
        const finalFormData = previewEnabled ? combinedFormData : combineFormData(leftFormData, rightFormData);
        
        // Convert states string back to array of objects for backend
        const formDataWithStatesArray = { ...finalFormData };
        if (formDataWithStatesArray.states && typeof formDataWithStatesArray.states === 'string' && formDataWithStatesArray.states.trim()) {
          formDataWithStatesArray.states = [{
            alias: formDataWithStatesArray.states.toLowerCase().replace(/\s+/g, '-'),
            name: formDataWithStatesArray.states
          }];
        } else {
          // Handle empty string, undefined, or non-string values
          formDataWithStatesArray.states = [];
        }

        // Get changed items for each array field
        const changedData = {};
        const deletedData = {};
        let hasChanges = false;
        
        // Check for changes in array fields - all array fields get type values (add, update, delete)
        arrayFields.forEach(fieldName => {
          const changedItems = getChangedItems(formDataWithStatesArray, originalData, fieldName);
          
          // Separate added/updated items from deleted items
          const addsAndUpdates = changedItems.filter(item => item.type === 'add' || item.type === 'update');
          const deletes = changedItems.filter(item => item.type === 'delete');
          
          if (addsAndUpdates.length > 0) {
            // Keep the 'type' property in the payload
            changedData[fieldName] = addsAndUpdates;
            hasChanges = true;
          }
          
          if (deletes.length > 0) {
            // Keep the 'type' property in the payload
            deletedData[fieldName] = deletes;
            hasChanges = true;
          }
        });
        
        // Handle non-array fields - compare simple fields
        simpleFields.forEach(fieldName => {
          const currentValue = formDataWithStatesArray[fieldName];
          const originalValue = originalData[fieldName];
          
          if (JSON.stringify(currentValue) !== JSON.stringify(originalValue)) {
            changedData[fieldName] = currentValue;
            hasChanges = true;
          }
        });
        
        if (hasChanges) {
          const fieldsToTransform = [
          ];
          const updatedFormData = reverseTransformData(changedData, fieldsToTransform);
          
          const result = await updateLocationGeographicalDetailsData(alias, updatedFormData, deletedData);

          if (result?.status === 200) {
            // Update originalData to reflect the new baseline after successful save
            const updatedOriginalData = { ...originalData };
            arrayFields.forEach(fieldName => {
              const currentArray = formDataWithStatesArray[fieldName] || [];
              updatedOriginalData[fieldName] = [...currentArray];
            });
            // Also update simple fields
            simpleFields.forEach(fieldName => {
              updatedOriginalData[fieldName] = formDataWithStatesArray[fieldName];
            });
            setOriginalData(updatedOriginalData);
            
            // Update form data for display (keep states as string for UI)
            const processedDataForDisplay = { ...formDataWithStatesArray };
            if (processedDataForDisplay.states && Array.isArray(processedDataForDisplay.states) && processedDataForDisplay.states.length > 0) {
              processedDataForDisplay.states = processedDataForDisplay.states[0].name || processedDataForDisplay.states[0].alias || '';
            }
            
            // Split data for two-column layout
            const { leftData, rightData } = splitFormData(processedDataForDisplay);
            setLeftFormData(leftData);
            setRightFormData(rightData);
            setCombinedFormData(processedDataForDisplay);
            
            setQuickSave(prev => !prev);
                        showSuccessToast(toast, "Geographical details updated successfully");
          }
        } else {
          // No changes detected
                    showInfoToast(toast, "No changes detected");
        }
      }
    } catch (error) {
      console.error('Error updating geographical details:', error);
            showErrorToast(toast, "Failed to update data");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="auto" pb="0px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Box>
          {previewEnabled ? (
            /* Single column layout for preview mode */
            <Box display={"flex"} flexDirection={"row"} alignItems={"flex-start"}>
              <Box flex={1} bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
                <Box fontSize="lg" fontWeight="bold" mb={4}>GeographicalDetails fields</Box>
                <Form 
                  ref={formRef}
                  schema={combinedSchema} 
                  uiSchema={combinedUiSchema(isSubmitting)} 
                  formData={combinedFormData} 
                  validator={validator} 
                  widgets={widgets}
                  onChange={handleCombinedFormChange}
                  fontsize={"14px"}
                />
              </Box>
            </Box>
          ) : (
            /* Two column layout with separate forms */
            <Box display={"flex"} flexDirection={"row"} alignItems={"flex-start"}>
              <Stack flexDirection="row" gap={4} alignItems="flex-start" flex={1}>
                <HStack alignItems="flex-start" flex={1}>
                  {/* Left Column */}
                  <Box flex={1} bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
                    <Box fontSize="lg" fontWeight="bold" mb={4}>GeographicalDetails fields</Box>
                    <Form
                      ref={formRef}
                      schema={leftColumnSchema}
                      uiSchema={leftColumnUiSchema(isSubmitting)}
                      formData={leftFormData}
                      validator={validator}
                      onChange={handleLeftFormChange}
                      widgets={widgets}
                      fontsize={"14px"}
                    />
                  </Box>
                  {/* Right Column */}
                  <Box flex={1} bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
                    <Form
                      schema={rightColumnSchema}
                      uiSchema={rightColumnUiSchema(isSubmitting)}
                      formData={rightFormData}
                      validator={validator}
                      onChange={handleRightFormChange}
                      widgets={widgets}
                      fontsize={"14px"}
                    />
                  </Box>
                </HStack>
              </Stack>
            </Box>
          )}
        </Box>
      )}
      
      {/* Fixed Save Button at Bottom */}
      <Box
        position="fixed"
        bottom={0}
        left="250px" // Account for sidebar width
        right={0}
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        p={4}
        zIndex={1000}
        display="flex"
      >
        <Box maxW="auto" display="flex" mx="auto">
          <Button
            colorScheme="blue"
            size="lg"
            width="auto"
            isLoading={isSubmitting}
            loadingText="Saving..."
            onClick={() => handleSubmit()}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>
      
      {/* Add padding to prevent content from being hidden behind fixed button */}
      <Box pb="100px" />
    </Box>
  );
};

export default GeographicalDetails;